<template>
  <div class="ov-page">
    <div class="ov-header">
      <div>
        <h1 class="ov-header__title">{{ offreVoyage.designation || $t('travelOffer.editOffer') }}</h1>
        <div class="mo-header__meta">
          <span v-if="offreVoyage.isActif" class="ov-badge ov-badge--success">{{ $t('travelOffer.activeStatus') }}</span>
          <span v-else class="ov-badge ov-badge--neutral">{{ $t('travelOffer.inactiveStatus') }}</span>
          <span v-if="offreVoyage.typeOffreVoyageDesignation" class="ov-badge ov-badge--navy ov-badge--plain">
            {{ offreVoyage.typeOffreVoyageDesignation }}
          </span>
        </div>
      </div>
      <div class="ov-header__actions">
        <button type="button" class="ov-btn ov-btn--secondary" @click="$router.push('/selectionnerOffreVoyage')">
          <v-icon small>mdi-arrow-left</v-icon>
          {{ $t('travelOffer.offerList') }}
        </button>
      </div>
    </div>

    <div v-if="offreVoyage.isActif" class="ov-note ov-note--warning mb-6">
      <v-icon small color="#92400e">mdi-lock-outline</v-icon>
      <span><strong>Offre active :</strong> la modification des informations, des programmes et des prix est verrouillée.</span>
    </div>

    <div class="ov-grid">
      <!-- ===== PROGRAMMES & PRIX ===== -->
      <div class="ov-stack">
        <!-- Programmes -->
        <div class="ov-card">
          <div class="ov-card__head">
            <div>
              <div class="ov-card__kicker">Planning</div>
              <div class="ov-card__title">
                Programmes rattachés
                <span class="ov-count">{{ programmeList.length }}</span>
              </div>
            </div>
            <button
              v-if="offreVoyage.isActif !== true"
              type="button"
              class="ov-btn ov-btn--primary ov-btn--sm"
              @click="dialogEditProg = true"
            >
              <v-icon small>mdi-plus</v-icon>
              Ajouter
            </button>
          </div>

          <v-data-table
            :headers="programmeHeaders"
            :items="programmeList"
            class="ov-table"
            no-data-text="Aucun programme rattaché"
          >
            <template v-slot:[`item.isExpired`]="{ item }">
              <span v-if="item.isExpired" class="ov-badge ov-badge--danger">Expiré</span>
              <span v-else class="ov-badge ov-badge--success">En cours</span>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
              <div v-if="offreVoyage.isActif !== true" class="ov-row-actions">
                <button type="button" class="ov-icon-btn" title="Modifier" @click="editerProgramme(item)">
                  <v-icon small>mdi-pencil-outline</v-icon>
                </button>
                <button type="button" class="ov-icon-btn ov-icon-btn--danger" title="Supprimer" @click="supprimerProgramme(item)">
                  <v-icon small>mdi-trash-can-outline</v-icon>
                </button>
              </div>
            </template>
          </v-data-table>
        </div>

        <!-- Prix -->
        <div class="ov-card">
          <div class="ov-card__head">
            <div>
              <div class="ov-card__kicker">Tarification</div>
              <div class="ov-card__title">
                Prix rattachés
                <span class="ov-count">{{ tarificationList.length }}</span>
              </div>
            </div>
            <button
              v-if="offreVoyage.isActif !== true"
              type="button"
              class="ov-btn ov-btn--primary ov-btn--sm"
              @click="dialogForPrice = true"
            >
              <v-icon small>mdi-plus</v-icon>
              Ajouter
            </button>
          </div>

          <v-data-table
            :headers="tarifHeaders"
            :items="tarificationList"
            class="ov-table"
            no-data-text="Aucun prix défini"
          >
            <template v-slot:[`item.prix`]="{ item }">
              <span class="mo-price">{{ item.prix }} <span>FCFA</span></span>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
              <div v-if="offreVoyage.isActif !== true" class="ov-row-actions">
                <button type="button" class="ov-icon-btn" title="Modifier" @click="editerTarification(item)">
                  <v-icon small>mdi-pencil-outline</v-icon>
                </button>
                <button type="button" class="ov-icon-btn ov-icon-btn--danger" title="Supprimer" @click="supprimerTarif(item)">
                  <v-icon small>mdi-trash-can-outline</v-icon>
                </button>
              </div>
            </template>
          </v-data-table>
        </div>
      </div>

      <!-- ===== INFORMATIONS GÉNÉRALES ===== -->
      <div class="ov-sticky">
        <div class="ov-card">
          <div class="ov-card__head">
            <div>
              <div class="ov-card__kicker">{{ $t('travelOffer.generalInfo') }}</div>
              <div class="ov-card__title">{{ $t('travelOffer.editOffer') }}</div>
            </div>
          </div>

          <div class="ov-card__body">
            <div class="ov-route mo-route">
              <div class="ov-route__city">
                <div class="ov-route__label">Départ</div>
                <div class="ov-route__value">{{ offreVoyage.villeDepartDesignation || '—' }}</div>
              </div>
              <div class="ov-route__line"></div>
              <div class="ov-route__city">
                <div class="ov-route__label">Destination</div>
                <div class="ov-route__value">{{ offreVoyage.villeDestinationDesignation || '—' }}</div>
              </div>
            </div>

            <div class="mo-form">
              <div class="ov-field">
                <label class="ov-label">Désignation</label>
                <input
                  v-model.trim="offreVoyage.designation"
                  :disabled="offreVoyage.isActif === true"
                  type="text"
                  class="ov-input"
                  placeholder="Désignation de l'offre"
                />
              </div>
              <div class="ov-field">
                <label class="ov-label">Type de l'offre</label>
                <input
                  v-model.trim="offreVoyage.typeOffreVoyageDesignation"
                  :disabled="offreVoyage.isActif === true"
                  type="text"
                  class="ov-input"
                  placeholder="Type d'offre"
                />
              </div>
              <div class="ov-field">
                <label class="ov-label">Ville de départ</label>
                <select v-model.trim="offreVoyage.villeDepartDesignation" :disabled="offreVoyage.isActif === true" class="ov-select">
                  <option value="">Sélectionner</option>
                  <option v-for="v in villesList" :key="v.designation" :value="v.designation">{{ v.designation }}</option>
                </select>
              </div>
              <div class="ov-field">
                <label class="ov-label">Ville de destination</label>
                <select v-model.trim="offreVoyage.villeDestinationDesignation" :disabled="offreVoyage.isActif === true" class="ov-select">
                  <option value="">Sélectionner</option>
                  <option v-for="v in villesList" :key="v.designation" :value="v.designation">{{ v.designation }}</option>
                </select>
              </div>
              <div class="ov-field">
                <label class="ov-label">Description</label>
                <textarea
                  v-model.trim="offreVoyage.description"
                  :disabled="offreVoyage.isActif === true"
                  class="ov-textarea"
                  rows="3"
                  placeholder="Description de l'offre..."
                ></textarea>
              </div>
            </div>
          </div>

          <div v-if="offreVoyage.isActif !== true" class="ov-card__foot">
            <button type="button" class="ov-btn ov-btn--primary ov-btn--block" @click="modifierOffreVoyageInfo">
              <v-icon small>mdi-content-save-outline</v-icon>
              Enregistrer les modifications
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== DIALOGUE PROGRAMME ===== -->
    <v-dialog v-model="dialogEditProg" persistent max-width="640px">
      <div class="ov-dialog">
        <div class="ov-dialog__head">
          <div class="ov-dialog__title">{{ formTitleProgramme }}</div>
        </div>
        <div class="ov-dialog__body">
          <div class="ov-row">
            <div class="ov-field">
              <label class="ov-label">Date de départ</label>
              <input v-model="editedProgramme.dateDepart" type="date" class="ov-input" />
            </div>
            <div class="ov-field">
              <label class="ov-label">Date d'arrivée</label>
              <input v-model="editedProgramme.dateArrivee" type="date" class="ov-input" />
            </div>
            <div class="ov-field">
              <label class="ov-label">Heure de départ</label>
              <input v-model="editedProgramme.heureDepart" type="time" class="ov-input" />
            </div>
            <div class="ov-field">
              <label class="ov-label">Heure d'arrivée (estimation)</label>
              <input v-model="editedProgramme.heureArrivee" type="time" class="ov-input" />
            </div>
            <div class="ov-field ov-col-full">
              <label class="ov-label">Autocar</label>
              <select v-model="editedProgramme.numeroBus" class="ov-select">
                <option :value="null" disabled>Sélectionner un autocar</option>
                <option v-for="bus in availableBusList" :key="bus.designation" :value="bus.designation">
                  {{ bus.designation }}
                </option>
              </select>
            </div>
          </div>
        </div>
        <div class="ov-dialog__foot">
          <button type="button" class="ov-btn ov-btn--secondary" @click="closeDialogProgramme()">Annuler</button>
          <button type="button" class="ov-btn ov-btn--primary" @click="saveOrUpdateProgramme()">
            {{ editedProgrammeIndex === -1 ? 'Ajouter' : 'Modifier' }}
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- ===== DIALOGUE PRIX ===== -->
    <v-dialog v-model="dialogForPrice" persistent max-width="560px">
      <div class="ov-dialog">
        <div class="ov-dialog__head">
          <div class="ov-dialog__title">{{ formTitlePrice }}</div>
        </div>
        <div class="ov-dialog__body">
          <div class="ov-row">
            <div class="ov-field ov-col-full">
              <label class="ov-label">Catégorie de voyageur</label>
              <select v-model="editedTarif.categorieVoyageurDesignation" class="ov-select">
                <option :value="null" disabled>Sélectionner</option>
                <option v-for="cat in referenceModeCategorieVoyageurList" :key="cat.designation" :value="cat.designation">
                  {{ cat.designation }}
                </option>
              </select>
            </div>
            <div class="ov-field">
              <label class="ov-label">Mode de l'offre</label>
              <select v-model="editedTarif.modeDesignation" class="ov-select">
                <option :value="null" disabled>Sélectionner</option>
                <option v-for="mode in referenceModeOffreVoyageList" :key="mode.designation" :value="mode.designation">
                  {{ mode.designation }}
                </option>
              </select>
            </div>
            <div class="ov-field">
              <label class="ov-label">Prix</label>
              <div class="ov-input-suffix">
                <input v-model="editedTarif.prix" type="number" min="0" class="ov-input" placeholder="0" />
                <span>FCFA</span>
              </div>
            </div>
          </div>
        </div>
        <div class="ov-dialog__foot">
          <button type="button" class="ov-btn ov-btn--secondary" @click="closeDialogPrice">Annuler</button>
          <button type="button" class="ov-btn ov-btn--primary" @click="saveOrUpdatePrice()">
            {{ editedTarifIndex === -1 ? 'Ajouter' : 'Modifier' }}
          </button>
        </div>
      </div>
    </v-dialog>

    <v-alert class="myalert alert-error" type="error" width="350px" dismissible>{{ errorMsg }}</v-alert>
    <v-alert class="myalert alert-success" type="success" width="350px" dismissible>{{ successMsg }}</v-alert>
    <v-overlay :value="overlay"><v-progress-circular indeterminate size="56" color="#159e72"></v-progress-circular></v-overlay>
  </div>
</template>

<script>
import axios from "axios";
import {
  API_RECUPERER_PRIX_PAR_OFFRE_VOYAGE,
  API_RECUPERER_PROGRAMME_PAR_OFFRE_VOYAGE,
  API_RECUPERER_LISTE_BUS_PAR_COMPAGNIE,
  API_CREER_OFFRE_VOYAGE,
  API_CREER_PROGRAMME_OFFRE_VOYAGE,
  API_OBTENIR_REFERENCE_PAR_PAR_FAMILLE,
  HEADERS,
  API_RATTACHER_PRIX_OFFRE_VOYAGE,
  API_OBTENIR_LISTE_DES_VILLES_DISPONIBLE,
  API_GET_ALL_CATEGORY,
} from "@/components/globalConfig/globalConstConfig";
import $ from "jquery";
import {
  showErrorMessage,
  showSuccessMessage,
  showWarningMessage,
} from "../messages/messageProcess";
export default {
  name: "ModifierOffreVoyage",

  data() {
    return {
      //VARIABLE COMMUNES
      overlay: false,
      programmeHeaders: [
        { text: 'Désignation', value: 'designation' },
        { text: 'Date départ', value: 'dateDepart' },
        { text: 'Date arrivée', value: 'dateArrivee' },
        { text: 'H. départ', value: 'heureDepart' },
        { text: 'H. arrivée', value: 'heureArrivee' },
        { text: 'Bus', value: 'numeroBus' },
        { text: 'Statut', value: 'isExpired', sortable: false },
        { text: 'Actions', value: 'actions', sortable: false },
      ],
      tarifHeaders: [
        { text: 'Désignation', value: 'designation' },
        { text: 'Catégorie', value: 'categorieVoyageurDesignation' },
        { text: 'Mode', value: 'modeDesignation' },
        { text: 'Prix', value: 'prix' },
        { text: 'Actions', value: 'actions', sortable: false },
      ],
      successMsg: null,
      errorMsg: null,
      warningMsg: null,

      // VARIABLES EN RAPPORT AVEC LES OFFRES DE VOYAGES

      offreVoyageObject: {
        data: {
          designation: null,
        },
      },

      offreVoyageToModify: {
        datas: [],
      },

      offreVoyage: {
        id: null,
        designation: null,
        description: null,
        compagnieTransportRaisonSociale: null,
        typeOffreVoyageDesignation: null,
        isActif: null,
        villeDepartDesignation: null,
        villeDestinationDesignation: null,
      },

      // VARIABLES EN RAPPORT AVEC LES PRIX D'OFFRE DE VOYAGE

      dialogForPrice: false,

      tarificationList: [],

      editedTarifIndex: -1,

      editedTarif: {
        designation: null,
        prix: null,
        modeDesignation: null,
        categorieVoyageurDesignation: null,
        offreVoyageDesignation: null,
      },

      defaultTarif: {
        designation: null,
        prix: null,
        modeDesignation: null,
        categorieVoyageurDesignation: null,
        offreVoyageDesignation: null,
      },

      priceObject: {
        datas: [],
      },

      objectToSendReferenceModeOffre: {
        datas: [],
      },

      objectToSendReferenceModeCategorieVoyageur: {
        datas: [],
      },

      referenceModeOffreVoyage: {
        referenceFamilleDesignation: "referenceFamilleMode",
      },

      referenceCategorieVoyageur: {
        referenceFamilleDesignation: "referenceFamilleCategorieVoyaeur",
      },

      retrieveObjectCategory: {
        data: {
          compagnieTransportId:
            this.$store.state.userAuthentified.compagnieTransportId,
        },
      },

      referenceModeOffreVoyageList: [],

      referenceModeCategorieVoyageurList: [],

      // VARIABLES EN RAPPORT AVEC LES PROGRAMMES

      dialogEditProg: false,

      programmeList: [],

      editedProgrammeIndex: -1,

      editedProgramme: {
        offreVoyageDesignation: null,
        numeroBus: null,
        dateDepart: null,
        dateArrivee: null,
        heureDepart: null,
        heureArrivee: null,
      },

      defaultProgramme: {
        offreVoyageDesignation: null,
        designation: null,
        numeroBus: null,
        dateDepart: null,
        dateArrivee: null,
        heureDepart: null,
        heureArrivee: null,
      },

      programmeObject: {
        datas: [],
      },

      // VARIABLES EN RAPPORT AVEC LES BUS
      availableBusList: [],

      busObject: {
        data: {
          raisonSociale: null,
        },
      },

      villesList: [],

      villeValue: {},
    };
  },

  computed: {
    programmesEnCoursCount() {
      if (!this.programmeList || this.programmeList.length === 0) return 0;

      return this.programmeList.filter((p) => {
        if (!p || p.isDeleted === true) return false;
        return p.isExpired !== true;
      }).length;
    },

    formTitlePrice() {
      return this.editedTarifIndex === -1 ? "Nouveau Tarif" : "Modifier le tarif";
    },

    formTitleProgramme() {
      return this.editedProgrammeIndex === -1
        ? "Nouveau Programme"
        : "Modifier le programme";
    },

  },

  methods: {
    // MODIFIER UNE OFFRE DE VOYAGE (informations générales)
    async modifierOffreVoyageInfo() {
      if (this.offreVoyage.isActif === true) return;

      this.offreVoyageToModify.datas.push(this.offreVoyage);
      this.overlay = true;

      await axios
        .put(API_CREER_OFFRE_VOYAGE, this.offreVoyageToModify, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          if (response.status == 200) {
            if (response.data.status.code == 800) {
              this.successMsg = response.data.status.message;
              showSuccessMessage();
              this.offreVoyageToModify.datas = [];
            } else {
              this.errorMsg = response.data.status.message;
              showErrorMessage();
              this.offreVoyageToModify.datas = [];
            }
          } else if (response.status == 204) {
            this.warningMsg = "Erreur , lors de la création de l'offre de voyage";
            showWarningMessage();
            this.offreVoyageToModify.datas = [];
          } else {
            this.errorMsg = "Erreur , opération de création impossible";
            showErrorMessage();
            this.offreVoyageToModify.datas = [];
          }
        })
        .catch((e) => {
          this.errorMsg = e;
          showErrorMessage();
          this.offreVoyageToModify.datas = [];
        })
        .finally(() => {
          this.overlay = false;
        });
    },

    // RECUPERER LA LISTE DES VILLES ENREGISTRÉES
    async getAllCities() {
      this.loading = false;
      await axios
        .post(API_OBTENIR_LISTE_DES_VILLES_DISPONIBLE, this.villeValue, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          this.villesList = response.data.items;
        })
        .catch((e) => {
          this.errorMsg = e;
          showErrorMessage();
        });
    },

    // CREER OU MODIFIER UN PRIX
    saveOrUpdatePrice() {
      if (this.editedTarifIndex > -1) {
        this.modifierTarifOffreVoyage();
      } else {
        this.creerTarifOffreVoyage();
      }
    },

    // CREER OU MODIFIER UN PRIX
    saveOrUpdateProgramme() {
      if (this.editedProgrammeIndex > -1) {
        this.modifierProgrammeOffreVoyage();
        this.showProgrammeList = false;
      } else {
        this.creerProgrammeOffreVoyage();
        this.showProgrammeList = false;
      }
    },

    // FERMER LA BOITE DE DIALOGUE DES PRIX
    closeDialogPrice() {
      this.dialogForPrice = false;
      this.$nextTick(() => {
        this.editedTarif = Object.assign({}, this.defaultTarif);
        this.editedTarifIndex = -1;
      });
    },

    // FERMER LA BOITE DE DIALOGUE DES PROGRAMMES
    closeDialogProgramme() {
      this.dialogEditProg = false;
      this.$nextTick(() => {
        this.editedProgramme = Object.assign({}, this.defaultProgramme);
        this.editedProgrammeIndex = -1;
      });
    },

    //OBTENIR REFERENCE DESIGNATION TYPE OFFRE DE VOYAGE
    async obtenirReferenceModeOffreVoyage() {
      this.objectToSendReferenceModeOffre.datas.push(
        this.referenceModeOffreVoyage
      );
      await axios
        .post(
          API_OBTENIR_REFERENCE_PAR_PAR_FAMILLE,
          this.objectToSendReferenceModeOffre,
          { headers: HEADERS(this.$store.state.userAuthentified.token) }
        )
        .then((response) => {
          this.referenceModeOffreVoyageList = response.data.items;
        })
        .catch((e) => {
          this.errorMsg = e;
          $(".alert-error").fadeIn();
          setTimeout(function () {
            $(".alert-error").fadeOut();
          }, 4000);
        });
    },

    //OBTENIR REFERENCE DESIGNATION CATEGORIE VOYAGEUR
    async getAllCategoryByCompagnieTransport() {
      await axios
        .post(API_GET_ALL_CATEGORY, this.retrieveObjectCategory, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          this.referenceModeCategorieVoyageurList  =
            response.data.item.referenceDTOS;
        })
        .catch((e) => {
          this.errorMsg = e;
          showErrorMessage();
        });
    },

    // CREER UN NOUVEAU TARIF
    async creerTarifOffreVoyage() {
      this.overlay = true;
      this.editedTarif.offreVoyageDesignation = this.offreVoyage.designation;

      this.priceObject.datas.push(this.editedTarif);
      await axios
        .post(API_RATTACHER_PRIX_OFFRE_VOYAGE, this.priceObject, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          if (response.status == 200) {
            if (response.data.status.code == 800) {
              this.successMsg = response.data.status.message;
              showSuccessMessage();
              this.getAllPriceByTravelOffers();
              this.priceObject.datas = [];
            } else {
              this.errorMsg = response.data.status.message;
              showErrorMessage();
              this.priceObject.datas = [];
            }
          } else if (response.status == 204) {
            this.warningMsg =
              "Erreur , lors de la création de l'offre de voyage";
            showWarningMessage();
            this.priceObject.datas = [];
          } else {
            this.errorMsg = "Erreur , opération de création impossible";
            showErrorMessage();
            this.priceObject.datas = [];
          }
        })
        .catch((e) => {
          this.errorMsg = e;
          showErrorMessage();
          this.priceObject.datas = [];
        })
        .finally(() => {
          this.overlay = false;
          this.dialogForPrice = false;
        });
    },

    // MODIFIER UN TARIF
    async modifierTarifOffreVoyage() {
      this.overlay = true;
      this.editedTarif.offreVoyageDesignation = this.offreVoyage.designation;

      this.priceObject.datas.push(this.editedTarif);
      await axios
        .put(API_RATTACHER_PRIX_OFFRE_VOYAGE, this.priceObject, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          if (response.status == 200) {
            if (response.data.status.code == 800) {
              this.successMsg = response.data.status.message;
              showSuccessMessage();
              Object.assign(
                this.tarificationList[this.editedTarifIndex],
                this.editedTarif
              );
              this.priceObject.datas = [];
            } else {
              this.errorMsg = response.data.status.message;
              showErrorMessage();
              this.priceObject.datas = [];
            }
          } else if (response.status == 204) {
            this.warningMsg =
              "Erreur , lors de la création de l'offre de voyage";
            showWarningMessage();
            this.priceObject.datas = [];
          } else {
            this.errorMsg = "Erreur , opération de création impossible";
            showErrorMessage();
            this.priceObject.datas = [];
          }
        })
        .catch((e) => {
          this.errorMsg = e;
          showErrorMessage();
          this.priceObject.datas = [];
        })
        .finally(() => {
          this.overlay = false;
          this.dialogForPrice = false;
        });
    },

    // RETOURNER LA DATE SOUS FORME JJ-MM-AAAA
    returnDateFormatted(dateFormatted) {
      let date = new Date(dateFormatted);
      let jour = date.getDate();
      let mois = date.getMonth() + 1;
      let annee = date.getFullYear();
      jour = String(jour).padStart(2, "0");
      mois = String(mois).padStart(2, "0");
      let dateFormat = `${jour}/${mois}/${annee}`;
      return dateFormat;
    },

    // MODIFIER UN PROGRAMME
    async modifierProgrammeOffreVoyage() {
      this.overlay = true;
      this.editedProgramme.offreVoyageDesignation =
        this.offreVoyage.designation;
      this.editedProgramme.dateDepart = this.returnDateFormatted(
        this.editedProgramme.dateDepart
      );
      this.editedProgramme.dateArrivee = this.returnDateFormatted(
        this.editedProgramme.dateArrivee
      );
      this.editedProgramme.numeroBus = this.returnBusNumber(
        this.editedProgramme.numeroBus
      );
      this.programmeObject.datas.push(this.editedProgramme);
      await axios
        .put(API_CREER_PROGRAMME_OFFRE_VOYAGE, this.programmeObject, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          if (response.status == 200) {
            if (response.data.status.code == 800) {
              this.successMsg = response.data.status.message;
              showSuccessMessage();
              this.editedProgramme.dateDepart = this.returnOriginalDate(
                this.editedProgramme.dateDepart
              );
              this.editedProgramme.dateArrivee = this.returnOriginalDate(
                this.editedProgramme.dateArrivee
              );
              Object.assign(
                this.programmeList[this.editedProgrammeIndex],
                this.editedProgramme
              );
              this.programmeObject.datas = [];
            } else {
              this.errorMsg = response.data.status.message;
              showErrorMessage();

              this.programmeObject.datas = [];
            }
          } else if (response.status == 204) {
            this.warningMsg =
              "Erreur , lors de la création de l'offre de voyage";
            showWarningMessage();
            this.programmeObject.datas = [];
          } else {
            this.errorMsg = "Erreur , opération de création impossible";
            showErrorMessage();
            this.programmeObject.datas = [];
          }
        })
        .catch((e) => {
          this.errorMsg = e;
          showErrorMessage();
          this.programmeObject.datas = [];
        })
        .finally(() => {
          this.overlay = false;
          this.dialogEditProg = false;
        });
    },

    // CREER UN PROGRAMME
    async creerProgrammeOffreVoyage() {
      this.overlay = true;
      this.editedProgramme.offreVoyageDesignation =
        this.offreVoyage.designation;
      this.editedProgramme.dateDepart = this.returnDateFormatted(
        this.editedProgramme.dateDepart
      );
      this.editedProgramme.dateArrivee = this.returnDateFormatted(
        this.editedProgramme.dateArrivee
      );
      this.editedProgramme.numeroBus = this.returnBusNumber(
        this.editedProgramme.numeroBus
      );
      this.programmeObject.datas.push(this.editedProgramme);
      await axios
        .post(API_CREER_PROGRAMME_OFFRE_VOYAGE, this.programmeObject, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          if (response.status == 200) {
            if (response.data.status.code == 800) {
              this.successMsg = response.data.status.message;
              showSuccessMessage();
              this.programmeObject.datas = [];
            } else {
              this.errorMsg = response.data.status.message;
              showErrorMessage();

              this.programmeObject.datas = [];
            }
          } else if (response.status == 204) {
            this.warningMsg =
              "Erreur , lors de la création de l'offre de voyage";
            showWarningMessage();
            this.programmeObject.datas = [];
          } else {
            this.errorMsg = "Erreur , opération de création impossible";
            showErrorMessage();
            this.programmeObject.datas = [];
          }
        })
        .catch((e) => {
          this.errorMsg = e;
          showErrorMessage();
          this.programmeObject.datas = [];
        })
        .finally(() => {
          this.overlay = false;
          this.dialogEditProg = false;
          this.getPlanningByOffersTravel();
        });
    },

    // SUPPRIMER UN PROGRAMME (local)
    supprimerProgramme(item) {
      this.programmeList = this.programmeList.filter((p) => p !== item);
    },

    // SUPPRIMER UN TARIF (local)
    supprimerTarif(item) {
      this.tarificationList = this.tarificationList.filter((t) => t !== item);
    },

    // EDITER UN PROGRAMME
    editerTarification(item) {
      this.editedTarifIndex = this.tarificationList.indexOf(item);
      this.editedTarif = Object.assign({}, item);
      this.dialogForPrice = true;
    },

    // EDITER UN PROGRAMME
    editerProgramme(item) {
      this.editedProgrammeIndex = this.programmeList.indexOf(item);
      this.editedProgramme = Object.assign({}, item);
      this.dialogEditProg = true;
    },

    //OBTENIR LISTE DES OFFRES DE VOYAGES DISPONIBLES PAR COMPAGNIE
    async getAllAvailableBus() {
      this.busObject.data.raisonSociale =
        this.$store.state.userAuthentified.compagnieTransportRaisonSociale;
      this.loading = true;
      await axios
        .post(API_RECUPERER_LISTE_BUS_PAR_COMPAGNIE, this.busObject, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          if (response.status == 200) {
            if (response.data.status.code != 800) {
              this.errorMsg = response.data.status.message;
              showErrorMessage();
            } else {
              this.availableBusList = response.data.items;
            }
          } else {
            this.errorMsg = "Erreur";
            showErrorMessage();
          }
        })
        .catch((e) => {
          this.errorMsg = e;
        });
    },

    //OBTENIR LA LISTE DES TARIFICATIONS PAR OFFRE DE VOYAGE
    async getAllPriceByTravelOffers() {
      this.offreVoyageObject.data.designation = this.offreVoyage.designation;
      await axios
        .post(API_RECUPERER_PRIX_PAR_OFFRE_VOYAGE, this.offreVoyageObject, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          if (response.data.status.code == 800) {
            this.tarificationList = response.data.items;
          } else {
            this.tarificationList = [];
          }
        })
        .catch((e) => {
          this.errorMsg = e;
          this.tarificationList = [];
          showErrorMessage();
        });
    },

    // RETOURNER LE NUMERO DU BUS
    returnBusNumber(designationBus) {
      var numeroBus;
      this.availableBusList.forEach((element) => {
        if (element.designation == designationBus) {
          numeroBus = element.numero;
        }
      });
      return numeroBus;
    },

    // RETOURNER LA DESIGNATION DU BUS
    returnBusDesignation(numeroBus) {
      var designationBus;
      this.availableBusList.forEach((element) => {
        if (element.numero == numeroBus) {
          designationBus = element.designation;
        }
      });
      return designationBus;
    },

    // METHODE PERMETTANT DE CONVERTIR CE FORMAT "DD/MM/YY" EN "YY-MM-DD"
    returnOriginalDate(date) {
      var formattedDate = date.split("/").reverse().join("-");
      return formattedDate;
    },

    //OBTENIR LA LISTE DES PROGRAMMES PAR OFFRE DE VOYAGE
    async getPlanningByOffersTravel() {
      this.offreVoyageObject.data.designation = this.offreVoyage.designation;
      await axios
        .post(
          API_RECUPERER_PROGRAMME_PAR_OFFRE_VOYAGE,
          this.offreVoyageObject,
          { headers: HEADERS(this.$store.state.userAuthentified.token) }
        )
        .then((response) => {
          if (response.data.status.code == 800) {
            response.data.items.forEach((element) => {
              var prog = {
                createdAt: element.createdAt,
                dateArrivee: this.returnOriginalDate(element.dateArrivee),
                dateDepart: this.returnOriginalDate(element.dateDepart),
                designation: element.designation,
                heureArrivee: element.heureArrivee,
                heureDepart: element.heureDepart,
                id: element.id,
                isDeleted: element.isDeleted,
                isExpired: element.isExpired,
                nombrePlaceDisponible: element.nombrePlaceDisponible,
                numeroBus: element.numeroBus,
                offreVoyageDesignation: element.offreVoyageDesignation,
              };
              this.programmeList.push(prog);
            });
          } else {
            this.programmeList = [];
          }
        })
        .catch((e) => {
          this.errorMsg = e;
          this.programmeList = [];
          showErrorMessage();
        });
    },

    // STOCKAGE DES INFORMATIONS DE L'OFFRE DE VOYAGE SELECTIONNÉ POUR L'EDITION FUTURE
    async editerOffreVoyage() {
      if (localStorage.getItem("offreVoyage")) {
        try {
          const offreVoyageEditing = JSON.parse(
            localStorage.getItem("offreVoyage")
          );
          this.offreVoyage.id = offreVoyageEditing.id;
          this.offreVoyage.designation = offreVoyageEditing.designation;
          this.offreVoyage.description = offreVoyageEditing.description;
          this.offreVoyage.typeOffreVoyageDesignation =
            offreVoyageEditing.typeOffreVoyageDesignation;
          this.offreVoyage.isActif = offreVoyageEditing.isActif;
          this.offreVoyage.villeDepartDesignation =
            offreVoyageEditing.villeDepartDesignation;
          this.offreVoyage.villeDestinationDesignation =
            offreVoyageEditing.villeDestinationDesignation;
          await this.getAllPriceByTravelOffers();
          await this.getPlanningByOffersTravel();
          localStorage.removeItem("offreVoyage");
        } catch (error) {
          localStorage.removeItem("offreVoyage");
        }
      } else {
        this.$router.replace({ path: "/selectionnerOffreVoyage" });
      }
    },
  },

  watch: {
    dialogForPrice(val) {
      val || this.closeDialogPrice();
    },
  },

  mounted() {
    this.editerOffreVoyage();
    this.getAllAvailableBus();
    this.obtenirReferenceModeOffreVoyage();
    this.getAllCategoryByCompagnieTransport()
    //this.obtenirReferenceModeCategorieVoyageur();
    this.getAllCities();
  },
};
</script>

<style lang="scss" scoped>
@import "../../styles/admin/ui";

.mo-header__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.mo-route {
  padding: 14px 16px;
  margin-bottom: 20px;
  border-radius: 12px;
  background: $ov-soft;
}

.mo-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.mo-price {
  font-weight: 800;
  color: $ov-navy;

  span {
    font-size: 11px;
    font-weight: 700;
    color: $ov-subtle;
  }
}
</style>
